/**
 * WeightPhotoCard.tsx — the deferred-weight-photo prompt (Wave 11), shared.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: photo-evidence surfacing.
 *
 * WHY A MODULE OF ITS OWN: the prompt began file-private in Dashboard.tsx, but
 * the moment a patient is MOST likely to act on the photo they deferred is the
 * seconds after they pay — so ThankYouPage mounts the same card. One component,
 * one upload state machine, one set of copy: two implementations would drift on
 * exactly the surface where drift misleads a patient about what their clinician
 * has seen.
 *
 * ── MULTI-PHOTO (2026-08-27, CTO) ──────────────────────────────────────────
 * The card no longer ends at the first photo. clinic-service takes up to five
 * photos per assessment (MAX_WEIGHT_PHOTOS) and keeps the patient lane open until
 * the linked order is DISPATCHED, so a patient who realises the first shot was
 * blurry — or whose clinician asked for another angle — can add more from right
 * here. The upload machine itself now lives in photos/WeightPhotoUploads.tsx,
 * shared with the plan-selection block: this file is the copy and the framing.
 *
 * ⚠️ THE COUNT IS WHAT THIS CARD HAS CONFIRMED STORED, AND IT STARTS AT ZERO FOR
 * A GOOD REASON. `GET mine/weight-photo` answers `{ photoPending }` and nothing
 * else — there is no patient-facing count read. But the card only mounts when
 * `photoPending` is TRUE, and the server computes that as "the deferral marker
 * is set AND no photoRef is stored" — so at mount there are demonstrably zero
 * photos on file, and counting up from there is exact rather than optimistic.
 * If a count read ever arrives, seed it; do NOT start guessing before then.
 *
 * TA-06: a failed upload keeps the control to retry and never claims success.
 * Category-safe copy, and the ASK itself comes from photos/weightPhotoCopy.ts —
 * this card used to write its own ("a scale reading only"), which asked for the
 * opposite of clinic-service's q14 seed on the very surface a deferring patient
 * finally acts on.
 *
 * ── TWO VOICES, ONE UPLOAD MACHINE (2026-09-06) ────────────────────────────
 * `variant="asked"` is everything above: we KNOW a photo is owed (the server
 * said `photoPending`), so the card states the obligation and takes the top of
 * the grid.
 *
 * `variant="offered"` states NOTHING. It exists because a patient asked for
 * evidence through the staff lane is invisible to the only patient-facing read
 * there is — `GET mine/weight-photo` answers the legacy q14 deferral marker and
 * cannot see a `WeightEvidenceRequest` — while clinic-service's upload lane is
 * deliberately held OPEN for exactly that patient. So the lane had no door on
 * it. The offered voice is that door: a quiet, optional route that lets someone
 * who was telephoned or emailed act, without asserting an obligation to the
 * patients who owe nothing. It is NOT an inference about what is owed and must
 * never be reworded into one — see EVIDENCE_SELF_SERVE_KINDS in
 * lib/dashboardSummary, and the [SERVER CONTRACT] block beside it for the read
 * that would let this card name the ask instead.
 */
import {
  useWeightPhotoUploads,
  WeightPhotoCapLine,
  WeightPhotoPicker,
  WeightPhotoTiles,
} from '../photos/WeightPhotoUploads';
import { WEIGHT_PHOTO_HELP, WEIGHT_PHOTO_NEVER } from '../photos/weightPhotoCopy';
import { Card, ErrorLine, Help } from '../ui/kit';
import { SectionTitle } from '../ui/sections';

/**
 * `asked` — the server told us a photo is owed. `offered` — nobody told us
 * anything and we are giving the patient a way in regardless. See the file
 * header; the difference is a clinical claim, not a style.
 */
export type WeightPhotoCardVariant = 'asked' | 'offered';

/**
 * The deferred-weight-photo prompt: shown full-width at the top of the grid when
 * the patient chose "I'll add this before dispatch" during the journey and
 * hasn't uploaded since. Uses the PATIENT lane — the authed, id-less grant the
 * server self-scopes to the caller's own latest submitted assessment (R41: the
 * id is never an input, so nothing here can reach anyone else's assessment).
 */
export function WeightPhotoCard({ variant = 'asked' }: { variant?: WeightPhotoCardVariant } = {}) {
  const uploads = useWeightPhotoUploads({ kind: 'patient' });
  const stored = uploads.storedCount;
  const offered = variant === 'offered';

  /* THE HEADING IS THE STATUS. A patient scanning their dashboard should be able
     to tell what we still need from them without reading a paragraph — and the
     three states are genuinely different asks, not one ask with a counter.

     ⚠️ THE OFFERED HEADING IS AN OFFER, NOT A STATUS, and the difference is the
     whole point of the variant: "Add your weight photo" tells a patient we are
     waiting on them, which on this route we do not know. Once a photo has
     actually been sent the two voices converge — "Weight photo added" is a fact
     either way. */
  const heading =
    stored === 0
      ? offered
        ? 'Need to send us a photo?'
        : 'Add your weight photo'
      : stored === 1
        ? 'Weight photo added'
        : `${stored} weight photos added`;

  /* Any tile that failed and has not been retried away. The card must never
     imply success while one of these is on screen (TA-06). */
  const failed = uploads.tiles.some((tile) => tile.status === 'failed');

  return (
    /* The ASKED card spans the grid at the top of the page, because it is the
       one thing the patient has to do. The OFFERED card takes a single column
       in the rail: an optional route that outranked the hero would read as an
       ask, which is the one thing it must not do. */
    <Card style={offered ? undefined : { gridColumn: '1 / -1' }}>
      <SectionTitle>{heading}</SectionTitle>
      {/* THE HEADING IS THE STATUS, SO THE ASK HAS TO BE SPELLED OUT HERE. This
          card is the LAST surface a deferring patient sees, often days after the
          questionnaire, and it used to say "a photo of the scale reading only" —
          the opposite of what q14 asked them for. It now says exactly what the
          seed and the plan step say, from the one shared constant. */}
      {stored === 0 ? (
        <>
          <Help style={{ marginBottom: 'var(--gps-s-1)' }}>
            {offered
              ? /* CONDITIONAL, AND EVERY WORD OF IT IS. It names the two ways an
                   ask actually reaches a patient today (a call or an email from
                   the team) and claims nothing about this patient's own record,
                   because the portal cannot see whether a request exists. */
                'If our team or your clinician has asked you for a weight photo — by phone or by email — you can send it here.'
              : 'You chose to add this before your order is dispatched.'}
          </Help>
          <Help>{WEIGHT_PHOTO_HELP}</Help>
        </>
      ) : (
        <Help>
          Thanks — your clinician will see it with your review. You can add another angle any time
          before your order is dispatched.
        </Help>
      )}

      {uploads.remaining > 0 && !uploads.laneClosed ? (
        <WeightPhotoPicker
          id="weight-photo-input"
          /* "Upload now" is load-bearing: it is the control the dashboard and
             thank-you specs identify this card by, and it is the right verb for
             the first ask. Later photos are additions, not the thing owed. */
          label={stored === 0 ? (offered ? 'Send a photo' : 'Upload now') : 'Add another photo'}
          busyLabel="Uploading…"
          busy={uploads.busy}
          /* Never the page's primary button on the offered route — a green CTA
             in the rail competes with whatever the hero is actually asking for. */
          variant={stored === 0 && !offered ? 'primary' : 'ghost'}
          onFiles={uploads.addFiles}
        />
      ) : null}

      <WeightPhotoTiles uploads={uploads} />

      {failed ? <ErrorLine>Sorry — the upload did not work. Please try again.</ErrorLine> : null}

      <div style={{ marginTop: 'var(--gps-s-3)' }}>
        <WeightPhotoCapLine uploads={uploads} />
      </div>

      {/* The one true constraint, same words as every other surface. */}
      <p style={{ fontSize: '12.5px', color: 'var(--gps-muted)', margin: 'var(--gps-s-2) 0 0', lineHeight: 1.45 }}>
        {WEIGHT_PHOTO_NEVER}
      </p>
    </Card>
  );
}
